import { useArtwork } from "@design-lab/entities/artwork";
import { useCurrentComposition } from "@design-lab/entities/composition";
import { useProcessingTooltip } from "@design-lab/shared/hooks/useProcessingTooltip";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useExperiments } from "@design-lab/shared/lib/config";
import { shouldReorderDesignLabTabs } from "@design-lab/shared/lib/experiments";
import { Button } from "@design-lab/shared/ui";
import { Tooltip, TooltipTrigger } from "@design-lab/shared/ui/design-system/Tooltip";
import { useTab } from "@design-lab/shared/ui/layouts/state";
import { TabCard } from "@design-lab/shared/ui/layouts/TabCard";
import { ChevronRight } from "lucide-react";
import { useEnhancementsStarted } from "../model/useEnhancementsStarted";
import { DesignLabImageSelector } from "./DesignLabImageSelector";

type UploadArtworkProps = {
  side?: "front" | "back";
};

export function UploadArtwork({ side }: UploadArtworkProps = {}) {
  const { isDesktop } = useResponsive();
  const { isValid, canGoNext, next } = useTab();
  const { currentProduct } = useCurrentComposition();
  const { sides } = currentProduct?.surfaceMeta || {};

  const { front, back } = useArtwork();

  // Handle enhancements_started flow
  useEnhancementsStarted();

  const experiments = useExperiments();
  const showSubTabs = shouldReorderDesignLabTabs(experiments?.abDesignLabReorderTabs);

  const frontHasLoading = front.state.hasTag("loading");
  const backHasLoading = back.state.hasTag("loading");

  const shouldShowTooltip = frontHasLoading || backHasLoading;

  const { showTooltip, dismissTooltip } = useProcessingTooltip({
    shouldShow: shouldShowTooltip,
    enableTooltip: !showSubTabs
  });

  // If side prop is provided, only show that side
  if (side) {
    const shouldShowFooter = isDesktop && showSubTabs; // Next btn in footer in candidate variant
    const shouldShowInlineButton = isDesktop && canGoNext && !showSubTabs; // Inline btn in control variant

    return (
      <TabCard hideFooter={!shouldShowFooter} showNextButton={!isDesktop}>
        <DesignLabImageSelector side={side} />
        {shouldShowInlineButton && (
          <TooltipTrigger isOpen={showTooltip}>
            <Button
              data-testid="next-button"
              variant="destructive"
              size="xl"
              isDisabled={!isValid}
              onPress={next}
              className="text-background min-h-12 w-fit bg-blue-700 hover:bg-blue-800"
            >
              Next <ChevronRight />
            </Button>
            <Tooltip
              placement="right"
              onClose={dismissTooltip}
              className="bg-blue-700"
              arrowClassName="fill-blue-700"
            >
              <p>
                While the image is being processed
                <br />
                continue to add colors and more products.
              </p>
            </Tooltip>
          </TooltipTrigger>
        )}
      </TabCard>
    );
  }

  // Legacy behavior: show both sides (for when sub-tabs aren't used)
  return (
    <TabCard title="1. Upload" hideFooter showNextButton={!isDesktop}>
      <DesignLabImageSelector side={"front"} />
      {isDesktop && canGoNext && (
        <TooltipTrigger isOpen={showTooltip}>
          <Button
            data-testid="next-button"
            variant="destructive"
            size="xl"
            isDisabled={!isValid}
            onPress={next}
            className="text-background min-h-12 w-fit bg-blue-700 hover:bg-blue-800"
          >
            Next <ChevronRight />
          </Button>
          <Tooltip
            placement="right"
            onClose={dismissTooltip}
            className="bg-blue-700"
            arrowClassName="fill-blue-700"
          >
            <p>
              While the image is being processed
              <br />
              continue to add colors and more products.
            </p>
          </Tooltip>
        </TooltipTrigger>
      )}
      {sides?.back.length ? <DesignLabImageSelector side={"back"} /> : null}
    </TabCard>
  );
}
